import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useGeneralExams, useActiveSessions, useSessionHistory, useDeleteSession, useCompleteSession, useExamSections } from '../lib/api';
import type { StudySession } from '../lib/api';
import { motion, PageTransition, Stagger, Pressable, Modal } from '../lib/motion';

export function ExamSessionsPage() {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { data: examsData } = useGeneralExams();
  const { data: sectionsData } = useExamSections(examId ?? null);
  const { data: sessionsData } = useActiveSessions({ examId: examId ?? null });
  const { data: historyData } = useSessionHistory({ examId: examId ?? null });
  const deleteSession = useDeleteSession();
  const completeSession = useCompleteSession();
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const exam = examsData?.exams?.find(e => e.id === examId);
  const sections = sectionsData?.sections ?? [];
  const activeSessions = sessionsData?.sessions ?? [];
  const pastSessions = historyData?.sessions ?? [];

  const resumeSession = (session: StudySession) => {
    const sectionId = session.examSectionId ?? session.classSectionId;
    if (!sectionId) return;
    navigate(`/study/exam/${sectionId}?session=${session.id}`);
  };

  return (
    <PageTransition className="max-w-lg mx-auto px-4 pb-24">
      {/* Header */}
      <div className="flex items-center gap-1 pt-1 mb-6">
        <Link
          to={`/exam/${examId}`}
          aria-label="Back to exam dashboard"
          className="w-11 h-11 -ml-2 flex items-center justify-center rounded-full text-squishy-primary hover:bg-teal-50 transition-colors shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
          </svg>
        </Link>
        <div>
          <h1 className="font-display text-xl font-semibold text-squishy-dark tracking-tight leading-tight">All Sessions</h1>
          {exam && <p className="text-[11px] text-slate-500">{exam.code}</p>}
        </div>
      </div>

      {/* Active Sessions */}
      {activeSessions.length > 0 && (
        <section className="mb-7">
          <h2 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest px-1 mb-2.5">In Progress</h2>
          <Stagger className="space-y-2.5">
            {activeSessions.map(session => (
              <Stagger.Item key={session.id}>
                <SessionCardFull
                  session={session}
                  sections={sections}
                  onResume={() => resumeSession(session)}
                  onDelete={() => setConfirmDeleteId(session.id)}
                />
              </Stagger.Item>
            ))}
          </Stagger>
        </section>
      )}

      {/* Completed Sessions */}
      {pastSessions.length > 0 && (
        <section>
          <h2 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest px-1 mb-2.5">Completed</h2>
          <Stagger className="space-y-2.5">
            {pastSessions.map(s => {
              const accuracy = s.questionsAnswered > 0 ? Math.round((s.correctCount / s.questionsAnswered) * 100) : 0;
              const section = sections.find(sec => sec.id === s.examSectionId);
              const date = s.endedAt ? new Date(s.endedAt).toLocaleDateString() : new Date(s.startedAt).toLocaleDateString();

              return (
                <Stagger.Item key={s.id}>
                  <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-card flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-2xl flex flex-col items-center justify-center shrink-0 ${
                        accuracy >= 70 ? 'bg-emerald-50 text-emerald-600' : accuracy >= 50 ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-500'
                      }`}
                      aria-label={`${accuracy} percent correct`}
                    >
                      <span className="text-sm font-bold leading-none tabular-nums">{accuracy}%</span>
                      <span className="text-[8px] font-semibold uppercase tracking-wide mt-0.5 opacity-70">correct</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {section && <span className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-semibold truncate">{section.name}</span>}
                        <span className="text-[10px] text-slate-400 shrink-0">{date}</span>
                      </div>
                      <div className="text-sm font-semibold text-squishy-dark truncate mt-1">{s.label || 'All Topics'}</div>
                      <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                        <span>{s.questionsAnswered} answered</span>
                        <span aria-hidden>{'·'}</span>
                        <span className="text-emerald-600 font-semibold">+{s.xpEarned} XP</span>
                      </div>
                    </div>
                  </div>
                </Stagger.Item>
              );
            })}
          </Stagger>
        </section>
      )}

      {activeSessions.length === 0 && pastSessions.length === 0 && (
        <div className="text-center py-16">
          <div className="mx-auto w-14 h-14 rounded-3xl bg-teal-50 flex items-center justify-center mb-4" aria-hidden>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.7} stroke="currentColor" className="w-6 h-6 text-squishy-primary">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-sm font-medium text-slate-500">No sessions yet.</p>
          <p className="text-xs text-slate-400 mt-1">Start a session from the exam dashboard and it will appear here.</p>
        </div>
      )}

      {/* ── Delete Session Confirmation ─────────────────────────── */}
      <Modal open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <div className="bg-white rounded-3xl p-6 shadow-pop text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center" aria-hidden>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-6 h-6 text-red-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </div>
          <div>
            <p className="font-display text-base font-semibold text-squishy-navy">Discard this session?</p>
            <p className="text-xs text-slate-500 mt-1">Your progress will be lost and can't be recovered.</p>
          </div>
          <div className="flex gap-2">
            <Pressable
              onClick={() => setConfirmDeleteId(null)}
              className="flex-1 h-12 rounded-2xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Keep
            </Pressable>
            <Pressable
              haptic="warning"
              onClick={() => { if (confirmDeleteId) deleteSession.mutate(confirmDeleteId); setConfirmDeleteId(null); }}
              className="flex-1 h-12 rounded-2xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
            >
              Discard
            </Pressable>
          </div>
        </div>
      </Modal>
    </PageTransition>
  );
}

function SessionCardFull({ session, sections, onResume, onDelete }: {
  session: StudySession;
  sections: any[];
  onResume: () => void;
  onDelete: () => void;
}) {
  const progress = session.totalQuestions > 0
    ? Math.round((session.currentIndex / session.totalQuestions) * 100)
    : 0;
  const accuracy = session.questionsAnswered > 0
    ? Math.round((session.correctCount / session.questionsAnswered) * 100)
    : 0;
  const remaining = session.totalQuestions - session.currentIndex;
  const section = sections.find(s => s.id === session.examSectionId);

  const diff = Date.now() - new Date(session.startedAt).getTime();
  const mins = Math.floor(diff / 60000);
  const timeAgo = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : Math.floor(mins / 60) < 24 ? `${Math.floor(mins / 60)}h ago` : `${Math.floor(mins / 1440)}d ago`;

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-card">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onResume}>
          <div className="flex items-center gap-2 mb-1">
            {section && <span className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-semibold truncate">{section.name}</span>}
            <span className="text-[10px] text-slate-400 shrink-0">{timeAgo}</span>
          </div>
          <div className="text-sm font-semibold text-squishy-dark truncate">{session.label || 'All Topics'}</div>
          <div className="flex items-center gap-3 mt-2.5">
            <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-squishy-teal to-emerald-400 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <span className="text-[10px] font-semibold text-slate-500 tabular-nums shrink-0">{progress}%</span>
          </div>
          <div className="flex items-center gap-2 mt-1.5 text-[10px] text-slate-400">
            <span>{remaining} left</span>
            <span aria-hidden>{'·'}</span>
            <span>{accuracy}% correct</span>
            <span aria-hidden>{'·'}</span>
            <span className="text-emerald-600 font-semibold">+{session.xpEarned} XP</span>
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <Pressable
            onClick={onResume}
            className="bg-squishy-primary text-white px-4 h-11 rounded-xl text-xs font-semibold shadow-soft"
          >
            Resume
          </Pressable>
          <Pressable
            haptic="warning"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="min-h-[44px] px-2 text-slate-400 hover:text-red-500 text-[11px] font-medium text-center transition-colors"
          >
            Discard
          </Pressable>
        </div>
      </div>
    </div>
  );
}
